import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, ClipboardList, CheckSquare, ArrowLeft, List, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/Layout/DashboardLayout';
import RestDayWorkRequestForm from '../components/RestDayWork/RestDayWorkRequestForm';
import RestDayWorkRequestList from '../components/RestDayWork/RestDayWorkRequestList';
import RestDayWorkRequestDetail from '../components/RestDayWork/RestDayWorkRequestDetail';
import RestDayWorkAssignmentList from '../components/RestDayWork/RestDayWorkAssignmentList';
import RestDayWorkAmendmentDiff from '../components/RestDayWork/RestDayWorkAmendmentDiff';
import { CountBadge } from '../components/UI/CountBadge';
import { useTabPersistence } from '../hooks/usePersistence';
import { staffAPI } from '../services/api';

// Route: /self-service/rest-day-work
const RestDayWorkManagement = () => {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: staffAPI.getCurrentUser,
    select: (r) => r.data,
    staleTime: 5 * 60 * 1000,
  });

  const { data: pendingData } = useQuery({
    queryKey: ['restday-work-pending-count'],
    queryFn: staffAPI.getRestDayWorkPendingApproval,
    select: (r) => r.data,
    staleTime: 30_000,
  });

  const canRequestRestDayWork = user?.can_request_restday_work;
  const isApprover = user?.is_restday_work_approver;
  const isAdmin = user?.has_admin_access;
  const isWorker = user?.employment_classification === 'worker';

  const validTabs = useMemo(() => {
    const tabs = [];
    if (isAdmin) tabs.push('all-requests', 'pending-approval');
    if (canRequestRestDayWork) {
      if (!tabs.includes('all-requests')) tabs.push('my-requests');
      tabs.push('new-request');
    }
    if (isApprover && !tabs.includes('pending-approval')) tabs.push('pending-approval');
    if (isWorker && !canRequestRestDayWork && !isApprover && !isAdmin) tabs.push('my-assignments');
    if (tabs.length === 0) tabs.push('my-assignments');
    return tabs;
  }, [canRequestRestDayWork, isApprover, isAdmin, isWorker]);

  const defaultTab = useMemo(() => {
    if (isAdmin) return 'all-requests';
    if (canRequestRestDayWork) return 'my-requests';
    if (isApprover) return 'pending-approval';
    return 'my-assignments';
  }, [isAdmin, canRequestRestDayWork, isApprover]);

  const [activeTab, setActiveTab] = useTabPersistence('restday-work', defaultTab, validTabs);
  const [selectedRequestId, setSelectedRequestId] = React.useState(null);
  const [editingAmendmentId, setEditingAmendmentId] = React.useState(null);

  const pendingCount = Array.isArray(pendingData) ? pendingData.length : pendingData?.count || 0;

  const handleRequestCreated = () => {
    setEditingAmendmentId(null);
    setActiveTab(isAdmin ? 'all-requests' : 'my-requests');
  };

  const handleViewDetail = (requestId) => {
    setEditingAmendmentId(null);
    setSelectedRequestId(requestId);
  };

  const handleBackToList = () => {
    setSelectedRequestId(null);
    setEditingAmendmentId(null);
  };

  const handleEditAmendment = (amendmentId) => {
    setSelectedRequestId(null);
    setEditingAmendmentId(amendmentId);
  };

  const handleBackFromAmendment = () => {
    setEditingAmendmentId(null);
  };

  if (editingAmendmentId) {
    return (
      <DashboardLayout>
        <div className="space-y-6 bg-gray-50 min-h-screen">
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={handleBackFromAmendment}
                aria-label="Back to Rest Day Work Requests"
                title="Back to Rest Day Work Requests"
                className="icon-button flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-subsection-header">Edit Amendment</h1>
                <p className="text-body mt-1">Modify the rest day work request details, then submit for re-approval</p>
              </div>
            </div>
          </div>

          <RestDayWorkAmendmentDiff amendmentId={editingAmendmentId} />

          <RestDayWorkRequestForm
            editRequestId={editingAmendmentId}
            onSuccess={handleRequestCreated}
            currentUser={user}
          />
        </div>
      </DashboardLayout>
    );
  }

  if (selectedRequestId) {
    return (
      <DashboardLayout>
        <div className="space-y-6 bg-gray-50 min-h-screen">
          <RestDayWorkRequestDetail
            requestId={selectedRequestId}
            onBack={handleBackToList}
            currentUser={user}
            onViewDetail={handleViewDetail}
            onEditAmendment={handleEditAmendment}
          />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen">
        {/* Page Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-4">
            <Link
              to="/self-service"
              aria-label="Back to Self-Service"
              title="Back to Self-Service"
              className="icon-button flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-subsection-header">Rest Day Work Requests</h1>
              <p className="text-body mt-1">Request, manage, and track rest day / Sunday work assignments</p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
          {isAdmin && (
            <button
              onClick={() => setActiveTab('all-requests')}
              className={`tab-btn ${activeTab === 'all-requests' ? 'active' : ''}`}
            >
              <List className="w-4 h-4" />
              All Requests
            </button>
          )}
          {canRequestRestDayWork && (
            <>
              {!isAdmin && (
                <button
                  onClick={() => setActiveTab('my-requests')}
                  className={`tab-btn ${activeTab === 'my-requests' ? 'active' : ''}`}
                >
                  <ClipboardList className="w-4 h-4" />
                  My Requests
                </button>
              )}
              <button
                onClick={() => setActiveTab('new-request')}
                className={`tab-btn ${activeTab === 'new-request' ? 'active' : ''}`}
              >
                <Plus className="w-4 h-4" />
                New Request
              </button>
            </>
          )}
          {(isApprover || isAdmin) && (
            <button
              onClick={() => setActiveTab('pending-approval')}
              className={`tab-btn ${activeTab === 'pending-approval' ? 'active' : ''}`}
            >
              <CheckSquare className="w-4 h-4" />
              Pending Approval
              {pendingCount > 0 && (
                <CountBadge count={pendingCount} theme="orange" active={activeTab === 'pending-approval'} />
              )}
            </button>
          )}
          {isWorker && !canRequestRestDayWork && !isApprover && !isAdmin && (
            <button
              onClick={() => setActiveTab('my-assignments')}
              className={`tab-btn ${activeTab === 'my-assignments' ? 'active' : ''}`}
            >
              <Calendar className="w-4 h-4" />
              My Assignments
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'new-request' && canRequestRestDayWork && (
          <RestDayWorkRequestForm onSuccess={handleRequestCreated} currentUser={user} />
        )}
        {activeTab === 'my-requests' && (
          <RestDayWorkRequestList
            mode="my-requests"
            onViewDetail={handleViewDetail}
            currentUser={user}
          />
        )}
        {activeTab === 'all-requests' && isAdmin && (
          <RestDayWorkRequestList
            mode="all-requests"
            onViewDetail={handleViewDetail}
            currentUser={user}
          />
        )}
        {activeTab === 'pending-approval' && (isApprover || isAdmin) && (
          <RestDayWorkRequestList
            mode="pending-approval"
            onViewDetail={handleViewDetail}
            currentUser={user}
          />
        )}
        {activeTab === 'my-assignments' && (
          <RestDayWorkAssignmentList currentUser={user} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default RestDayWorkManagement;
